import React, { useState, useEffect } from "react";
import Card from "components/Card";
import { JobApplicantType, JobApplicationType } from "types/careers-types";
import { updateApplicantDetails, getApplicantJobApplications, updateJobApplicationStatus, createTestSessionForApplication, createVideoTestSessionForApplication } from "api/apiHelper";
import IconButton from "components/IconButton";
import Table from "components/Table";
import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import { Link, useNavigate } from "react-router-dom";
import { MdVisibility, MdOutlinePages } from "react-icons/md";
import { Video } from "lucide-react";
import { toast } from "react-toastify";
import RejectionConfirmationModal from "components/RejectionConfirmationModal";

const STATUS_OPTIONS = [
  { label: "New", value: "new" },
  { label: "In Review", value: "in-review" },
  { label: "Shortlisted", value: "shortlisted" },
  { label: "Interviewed", value: "interviewed" },
  { label: "Offered", value: "offered" },
  { label: "Rejected", value: "rejected" },
];

interface ApplicantsCardPropsType {
  applicantDetails: JobApplicantType;
}

const ApplicantCard = (props: ApplicantsCardPropsType) => {
  const [notes, setNotes] = useState(props.applicantDetails.notes);
  const [isEditing, setIsEditing] = useState(false);
  const [jobApplications, setJobApplications] = useState<JobApplicationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [processingTestId, setProcessingTestId] = useState<string | null>(null);
  const [processingVideoTestId, setProcessingVideoTestId] = useState<string | null>(null);
  const [rejectionModal, setRejectionModal] = useState<{
    isOpen: boolean;
    applicationId: string | null;
    applicantName: string;
    jobTitle: string;
    currentStatus: string;
  }>({
    isOpen: false,
    applicationId: null,
    applicantName: "",
    jobTitle: "",
    currentStatus: "",
  });
  const navigate = useNavigate();

  const handleNotesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(event.target.value);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    try {
      await updateApplicantDetails(props.applicantDetails.id, { notes });
      setIsEditing(false);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchJobApplications = async () => {
      if (props.applicantDetails.id) {
        try {
          setLoading(true);
          const response = await getApplicantJobApplications(props.applicantDetails.id);
          const applications = Array.isArray(response.data)
            ? response.data
            : response.data?.results || [];
          setJobApplications(applications);
        } catch (error) {
          console.error("Failed to fetch job applications:", error);
          setJobApplications([]);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchJobApplications();
  }, [props.applicantDetails.id]);

  const isTestExpired = (testSession: any): boolean => {
    if (!testSession || !testSession.expires_at) return true;
    return new Date(testSession.expires_at) < new Date();
  };

  const canSendFirstTest = (application: JobApplicationType): boolean => {
    const testSession = application.test_session;
    if (!testSession) return true; // No test session exists, can send
    // Don't allow sending if test is completed
    if (testSession.status === 'completed') return false;
    return isTestExpired(testSession); // Can send if expired (and not completed)
  };

  const canSendVideoTest = (application: JobApplicationType): boolean => {
    const videoTestSession = application.video_test_session;
    if (!videoTestSession) return true; // No video test session exists, can send
    // Don't allow sending if test is completed
    if (videoTestSession.status === 'completed') return false;
    return isTestExpired(videoTestSession); // Can send if expired (and not completed)
  };

  const handleSendFirstTest = async (applicationId: string) => {
    setProcessingTestId(applicationId);
    const notification = toast.loading("Sending first test...");

    try {
      await createTestSessionForApplication(applicationId);
      toast.update(notification, {
        render: "First test sent successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      // Refresh job applications
      const response = await getApplicantJobApplications(props.applicantDetails.id!);
      const applications = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setJobApplications(applications);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || "Failed to send first test";
      toast.update(notification, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setProcessingTestId(null);
    }
  };

  const handleSendVideoTest = async (applicationId: string) => {
    setProcessingVideoTestId(applicationId);
    const notification = toast.loading("Sending video test...");

    try {
      await createVideoTestSessionForApplication(applicationId);
      toast.update(notification, {
        render: "Video test sent successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      // Refresh job applications
      const response = await getApplicantJobApplications(props.applicantDetails.id!);
      const applications = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setJobApplications(applications);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || "Failed to send video test";
      toast.update(notification, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setProcessingVideoTestId(null);
    }
  };

  const handleStatusUpdate = async (applicationId: string, newStatus: string) => {
    setUpdatingStatusId(applicationId);
    const notification = toast.loading("Updating status...");

    try {
      await updateJobApplicationStatus(applicationId, newStatus);
      toast.update(notification, {
        render: newStatus === "rejected" 
          ? "Status updated and rejection email sent successfully"
          : "Status updated successfully",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      // Refresh job applications
      const response = await getApplicantJobApplications(props.applicantDetails.id!);
      const applications = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setJobApplications(applications);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || "Failed to update status";
      toast.update(notification, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleRejectionConfirm = async () => {
    if (rejectionModal.applicationId) {
      await handleStatusUpdate(rejectionModal.applicationId, "rejected");
      setRejectionModal({
        isOpen: false,
        applicationId: null,
        applicantName: "",
        jobTitle: "",
        currentStatus: "",
      });
    }
  };

  const handleRejectionCancel = () => {
    setRejectionModal({
      isOpen: false,
      applicationId: null,
      applicantName: "",
      jobTitle: "",
      currentStatus: "",
    });
  };

  const columnHelper = createColumnHelper<JobApplicationType>();
  const columns: ColumnDef<JobApplicationType, any>[] = [
    columnHelper.display({
      id: "job_opening",
      header: () => <TableHeader>JOB OPENING</TableHeader>,
      cell: (info) => {
        const application = info.row.original;
        const title = application.job_opening_name;
        const jobOpening = application.job_opening;
        
        let jobSlug: string = "";
        if (typeof jobOpening === 'string') {
          jobSlug = jobOpening;
        } else if (jobOpening && typeof jobOpening === 'object') {
          jobSlug = jobOpening.slug || jobOpening.id || "";
        }
        
        if (title && jobSlug) {
          return (
            <Link 
              to={`/careers/job/${jobSlug}`}
              onClick={(e) => e.stopPropagation()}
              className="hover:underline text-blue-600 dark:text-blue-400"
            >
              <TableCell>{title}</TableCell>
            </Link>
          );
        }
        
        return <TableCell>{title || jobSlug || "-"}</TableCell>;
      },
    }),
    columnHelper.accessor("applied_at", {
      id: "applied_at",
      header: () => <TableHeader>APPLIED AT</TableHeader>,
      cell: (info) => {
        const date = new Date(info.getValue());
        const formattedDate = date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        return <TableCell>{formattedDate}</TableCell>;
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => <TableHeader>STATUS</TableHeader>,
      cell: (info) => {
        const application = info.row.original;
        const status = info.getValue();
        const applicationId = application.id;
        const isUpdating = updatingStatusId === applicationId;

        const getStatusStyle = (status: string) => {
          switch (status) {
            case "new":
              return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700";
            case "in-review":
              return "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700";
            case "shortlisted":
              return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700";
            case "interviewed":
              return "bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700";
            case "offered":
              return "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700";
            case "rejected":
              return "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700";
            default:
              return "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600";
          }
        };

        const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          e.stopPropagation();
          const newStatus = e.target.value;
          if (applicationId && newStatus !== status) {
            // Show confirmation modal if rejecting
            if (newStatus === "rejected") {
              const applicantName = application.applicant?.name || props.applicantDetails.name || "Applicant";
              const jobTitle = application.job_opening_name || "Position";
              setRejectionModal({
                isOpen: true,
                applicationId: applicationId,
                applicantName: applicantName,
                jobTitle: jobTitle,
                currentStatus: status,
              });
              // Reset dropdown to current status
              e.target.value = status;
            } else {
              // For other status changes, update directly
              handleStatusUpdate(applicationId, newStatus);
            }
          }
        };

        const getArrowColor = (status: string) => {
          switch (status) {
            case "new":
              return "%233b82f6"; // blue-500
            case "in-review":
              return "%23eab308"; // yellow-500
            case "shortlisted":
              return "%23a855f7"; // purple-500
            case "interviewed":
              return "%234f46e5"; // indigo-500
            case "offered":
              return "%2316a34a"; // green-600
            case "rejected":
              return "%23dc2626"; // red-600
            default:
              return "%236b7280"; // gray-500
          }
        };

        return (
          <TableCell>
            <div className="inline-flex items-center gap-2">
              <select
                value={status}
                onChange={handleStatusChange}
                disabled={isUpdating || !applicationId}
                onClick={(e) => e.stopPropagation()}
                className={`
                  inline-flex items-center rounded-full border px-2.5 py-1 pr-8 text-xs font-semibold
                  cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all
                  disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90
                  ${getStatusStyle(status)}
                `}
                style={{ 
                  appearance: 'none',
                  backgroundImage: isUpdating 
                    ? 'none'
                    : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${getArrowColor(status)}' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.5rem center',
                }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {isUpdating && (
                <svg className="animate-spin h-3 w-3 text-gray-600 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
          </TableCell>
        );
      },
    }),
    columnHelper.display({
      id: "test_score",
      header: () => <TableHeader>TEST SCORE</TableHeader>,
      cell: (info) => {
        const testSession = info.row.original.test_session;
        if (!testSession || testSession.status !== "completed") {
          return <TableCell>-</TableCell>;
        }
        
        const score = testSession.score_percentage !== null 
          ? Number(testSession.score_percentage) 
          : null;
        
        if (score === null) {
          return <TableCell>-</TableCell>;
        }
        
        const scoreColor =
          score >= 70
            ? "bg-green-100 text-green-700 border-green-200"
            : score >= 50
            ? "bg-yellow-100 text-yellow-700 border-yellow-200"
            : "bg-red-100 text-red-700 border-red-200";
        
        return (
          <TableCell>
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${scoreColor}`}
            >
              {score.toFixed(0)}%
            </span>
          </TableCell>
        );
      },
    }),
    columnHelper.display({
      id: "view",
      header: () => <TableHeader>VIEW</TableHeader>,
      cell: (info) => {
        const application = info.row.original;
        return (
          <Link 
            to={`/careers/job-application/${application.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <TableCell className="!w-max !min-w-0 !pr-0">
              <MdVisibility className="h-6 w-6 text-gray-600 dark:text-gray-300" />
            </TableCell>
          </Link>
        );
      },
    }),
  ];

  const handleRowClick = (application: JobApplicationType) => {
    if (application.id) {
      navigate(`/careers/job-application/${application.id}`);
    }
  };

  return (
    <div>
      {rejectionModal.isOpen && (
        <RejectionConfirmationModal
          onClose={handleRejectionCancel}
          onConfirm={handleRejectionConfirm}
          applicantName={rejectionModal.applicantName}
          jobTitle={rejectionModal.jobTitle}
        />
      )}
      <Card extra={"w-full h-full px-3 py-5"}>
      <div className="mb-8 flex w-full items-center justify-between">
        <h4 className="px-2 text-xl font-bold text-navy-700 dark:text-white">
          Application Details
        </h4>
      </div>
      <div className="grid grid-cols-1 gap-4 px-2 sm:grid-cols-2 lg:grid-cols-3">
        <div className="flex flex-col items-start rounded-2xl bg-white bg-clip-border px-3 py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none">
          <p className="text-sm text-gray-600">Title</p>
          <p className="text-base font-medium text-navy-700 dark:text-white">
            {props.applicantDetails.id}
          </p>
        </div>

        <div className="flex flex-col items-start rounded-2xl bg-white bg-clip-border px-3 py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none">
          <p className="text-sm text-gray-600">Applicant name</p>
          <p className="text-base font-medium text-navy-700 dark:text-white">
            {props.applicantDetails.name}
          </p>
        </div>

        <div className="flex flex-col items-start rounded-2xl bg-white bg-clip-border px-3 py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none">
          <p className="text-sm text-gray-600">Contact</p>
          <p className="text-base font-medium text-navy-700 dark:text-white">
            {props.applicantDetails.contact}
          </p>
        </div>
        <div className="flex flex-col items-start rounded-2xl bg-white bg-clip-border px-3 py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none">
          <p className="text-sm text-gray-600">Email</p>
          <p className="text-base font-medium text-navy-700 dark:text-white">
            {props.applicantDetails.email}
          </p>
        </div>
        <div className="flex flex-col items-start rounded-2xl bg-white bg-clip-border px-3 py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none">
          <p className="text-sm text-gray-600">City</p>
          <p className="text-base font-medium text-navy-700 dark:text-white">
            {props.applicantDetails.city}
          </p>
        </div>
        <div className={`flex flex-col items-start rounded-2xl bg-white bg-clip-border px-3 py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none col-span-2`}>
          <div className="flex items-center justify-between w-full mt-1">
            <p className="text-sm text-gray-600">Notes</p>
            {!isEditing && (
              <IconButton
                onClick={handleEditClick}
                url="#"
                name="Edit Notes"
                icon="fas fa-edit"
                extraStyles="ml-2 text-blue-500 mb-3"
              />
            )}
          </div>
          {isEditing ? (
            <>
              <textarea
                className="text-base font-medium text-navy-700 dark:text-white bg-transparent border-none focus:outline-none w-full h-24 overflow-y-auto"
                value={notes}
                onChange={handleNotesChange}
              />
              <button
                onClick={handleSaveClick}
                className="mt-2 bg-brand-500 hover:bg-brand-600 text-white px-3 py-1 rounded"
              >
                Save
              </button>
            </>
          ) : (
            <div className="text-base font-medium text-navy-700 dark:text-white w-full h-24 overflow-y-auto break-words">
              {notes}
            </div>
          )}
        </div>
      </div>
      
      {/* Job Applications Table */}
      <div className="mt-8">
        <h4 className="px-2 text-xl font-bold text-navy-700 dark:text-white mb-4">
          Job Applications
        </h4>
        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Loading job applications...
          </div>
        ) : jobApplications.length > 0 ? (
          <Table<JobApplicationType>
            tableData={jobApplications}
            columns={columns}
            tableHeading=""
            onRowClick={handleRowClick}
          />
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No job applications found for this applicant.
          </div>
        )}
      </div>
      </Card>
    </div>
  );
};

export default ApplicantCard;